"use client";

import { DocumentSnapshot } from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
import { Grid, List, Search } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ContentModal from "../components/ContentModal";
import PublishContent from "../components/create/PublishContent";
import ContentCard from "../components/dashboard/ContentCard";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import dreamWorldProvider from "../DreamWorldProvider";
import {
  incrementAlbumItemCount,
  reduxAppendAlbumContent,
  reduxSetAlbumContent,
  removeLibraryItem,
  selectAlbumContent,
  selectAlbums,
  selectUserGlobalData,
  toggleLikeContent,
} from "../store/dreamworldSlice";
import type { AppDispatch } from "../store/store";
import type { Album, ContentType, LibraryItem } from "../types/library";
import PageLayout from "../components/ui/PageLayout";

type SortOption = "Newest" | "Oldest" | "Most Liked" | "Most Viewed";
type FavoriteOption = "All" | "Favorites";
type ViewMode = "grid" | "masonry" | "list";

const AlbumsPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { albumId } = useParams<{ albumId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const userGlobalData = useSelector(selectUserGlobalData);
  const [hasMoreContent, setHasMoreContent] = useState(true);
  const libraryContent = useSelector((state: any) =>
    selectAlbumContent(state, albumId!)
  );
  const albums = useSelector(selectAlbums);

  const [selectedItem, setSelectedItem] = useState<LibraryItem | null>(null);
  const [publishItem, setPublishItem] = useState<LibraryItem | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<ContentType | "All">("All");
  const [sortOrder, setSortOrder] = useState<SortOption>("Newest");
  const [favoriteFilter, setFavoriteFilter] = useState<FavoriteOption>("All");
  const [viewMode, setViewMode] = useState<ViewMode>("masonry");
  const [loading, setLoading] = useState(false);
  const [lastVisibleLibraryItem, setLastVisibleLibraryItem] =
    useState<DocumentSnapshot | null>(null);
  const [album, setAlbum] = useState<Album | null>(null);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddToAlbumModal, setShowAddToAlbumModal] = useState(false);
  const [selectedAlbumForAddition, setSelectedAlbumForAddition] = useState<
    string | null
  >(null);

  const getAlbumContent = async () => {
    try {
      if (userGlobalData && albumId) {
        setLoading(true);
        const albumData = await dreamWorldProvider.getAlbum(albumId);
        setAlbum(albumData);
        const lbC = await dreamWorldProvider.getAlbumContent(albumId);
        setLastVisibleLibraryItem(lbC.lastVisible);
        dispatch(reduxSetAlbumContent({ albumId, content: lbC.items }));
      }
    } catch (error) {
      console.error("Error fetching album content:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAlbumContent();
  }, [userGlobalData, albumId]);

  const stats = useMemo(() => {
    const totalItems = libraryContent.length;
    const totalLikedItems = libraryContent.filter(
      (item) => item.isLiked
    ).length;
    const totalViews = libraryContent.reduce(
      (sum, item) => sum + item.views,
      0
    );
    const totalLikes = libraryContent.reduce(
      (sum, item) => sum + item.likes,
      0
    );

    const typeBreakdown = libraryContent.reduce(
      (acc, item) => {
        acc[item.type] = (acc[item.type] || 0) + 1;
        return acc;
      },
      {} as Record<ContentType, number>
    );

    return {
      totalItems,
      totalLikedItems,
      totalViews,
      totalLikes,
      typeBreakdown,
    };
  }, [libraryContent]);

  const filteredContent = useMemo(() => {
    let items = [...libraryContent];

    if (searchTerm) {
      items = items.filter(
        (item) =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.tags.some((tag) =>
            tag.toLowerCase().includes(searchTerm.toLowerCase())
          )
      );
    }

    if (typeFilter !== "All") {
      items = items.filter((item) => item.type === typeFilter);
    }

    if (favoriteFilter === "Favorites") {
      items = items.filter((item) => item.isFavorite);
    }

    items.sort((a, b) => {
      console.log("Sorting items:", a, b);
      switch (sortOrder) {
        case "Newest":
          return b.createdAt.seconds - a.createdAt.seconds;
        case "Oldest":
          return a.createdAt.seconds - b.createdAt.seconds;
        case "Most Liked":
          return b.likes - a.likes;
        case "Most Viewed":
          return b.views - a.views;
        default:
          return 0;
      }
    });

    return items;
  }, [libraryContent, searchTerm, typeFilter, sortOrder, favoriteFilter]);
  const getMoreAlbumContent = useCallback(async () => {
    try {
      if (userGlobalData && hasMoreContent && albumId) {
        setLoading(true);
        const lbC = await dreamWorldProvider.getAlbumContent(
          albumId,
          lastVisibleLibraryItem!
        );
        setLastVisibleLibraryItem(lbC.lastVisible);
        dispatch(
          reduxAppendAlbumContent({
            albumId,
            content: lbC.items,
          })
        );
        if (!lbC.lastVisible) {
          setHasMoreContent(false);
        }
      }
    } catch (error) {
      console.error("Error fetching more album content:", error);
    } finally {
      setLoading(false);
    }
  }, [userGlobalData, hasMoreContent, albumId, lastVisibleLibraryItem, dispatch]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMoreContent && !loading) {
          getMoreAlbumContent();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMoreContent, loading, getMoreAlbumContent]);

  const handleShare = async (item: LibraryItem) => {
    const url = `https://dreworld.web.app/content/${item.id}`;
    const shareData = {
      title: "Check out this content on DreamWorld!",
      text: item.title,
      url: url,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setNotification("Link copied to clipboard!");
        setTimeout(() => setNotification(null), 3000);
      } catch (error) {
        console.error("Error copying to clipboard:", error);
        setNotification("Failed to copy link.");
        setTimeout(() => setNotification(null), 3000);
      }
    }
  };

  const handleDownload = async (item: LibraryItem) => {
    try {
      const response = await fetch(item.fullUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.title || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setNotification("Download started!");
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error("Error downloading file:", error);
      setNotification("Failed to download file.");
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleDelete = async () => {
    if (selectedItem) {
      try {
        await dreamWorldProvider.deleteContent(
          selectedItem.id,
          selectedItem.fullUrl
        );
        dispatch(removeLibraryItem(selectedItem.id));
        setSelectedItem(null);
        setShowDeleteModal(false);
        setNotification("Content deleted successfully.");
        setTimeout(() => setNotification(null), 3000);
      } catch (error) {
        console.error("Error deleting content:", error);
        setNotification("Failed to delete content.");
        setTimeout(() => setNotification(null), 3000);
      }
    }
  };

  const handleAddToAlbum = async () => {
    if (selectedItem && selectedAlbumForAddition) {
      try {
        await dreamWorldProvider.addContentToAlbum(
          selectedItem,
          selectedAlbumForAddition
        );
        dispatch(incrementAlbumItemCount(selectedAlbumForAddition));
        setShowAddToAlbumModal(false);
        setNotification("Content added to album.");
        setTimeout(() => setNotification(null), 3000);
      } catch (error) {
        console.error("Error adding to album:", error);
        setNotification("Failed to add to album.");
        setTimeout(() => setNotification(null), 3000);
      }
    }
  };

  const handlePublishClick = (item: LibraryItem) => {
    setPublishItem(item);
    setSelectedItem(null);
  };

  return (
    <div
      className="bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 min-h-screen text-white"
    >
      <PageLayout>
        <div className="max-w-screen-2xl mx-auto">
          {/* Header with Stats */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
            <div>
              <h1 className="text-lg sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {album?.name || "Album"}
              </h1>
              <p className="text-gray-400 mt-2 text-sm sm:text-base">
                {album?.description || "Content of the album"}
              </p>
            </div>
          </div>

          <div className="relative mb-8">
            {/* Background blur effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-500/10 rounded-2xl backdrop-blur-sm border border-white/10"></div>

            <div className="relative p-4 sm:p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                    {stats.totalItems}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Items
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-red-400 to-pink-400 bg-clip-text text-transparent">
                    {stats.totalLikedItems}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Liked Items
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                    {stats.totalViews > 999
                      ? `${(stats.totalViews / 1000).toFixed(1)}K`
                      : stats.totalViews}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Views
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-lg font-bold bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                    {stats.totalLikes > 999
                      ? `${(stats.totalLikes / 1000).toFixed(1)}K`
                      : stats.totalLikes}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-300 font-medium">
                    Likes
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filters and Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search your library..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <select
                value={typeFilter}
                onChange={(e) =>
                  setTypeFilter(e.target.value as ContentType | "All")
                }
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="All">All Types</option>
                <option value="Image">Images</option>
                <option value="Video">Videos</option>
                <option value="Text">Text</option>
                <option value="Audio">Audio</option>
              </select>

              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOption)}
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="Newest">Newest First</option>
                <option value="Oldest">Oldest First</option>
                <option value="Most Liked">Most Liked</option>
                <option value="Most Viewed">Most Viewed</option>
              </select>

              <select
                value={favoriteFilter}
                onChange={(e) =>
                  setFavoriteFilter(e.target.value as FavoriteOption)
                }
                className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="All">All Items</option>
                <option value="Favorites">Favorites Only</option>
              </select>

              <div className="flex bg-gray-800 border border-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("masonry")}
                  className={`p-2 rounded transition-colors ${
                    viewMode === "masonry"
                      ? "bg-purple-600"
                      : "hover:bg-gray-700"
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded transition-colors ${
                    viewMode === "list" ? "bg-purple-600" : "hover:bg-gray-700"
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Content Results */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-400 text-sm sm:text-base">
              {filteredContent.length}{" "}
              {filteredContent.length === 1 ? "item" : "items"} found
            </p>
          </div>

          {loading === true && libraryContent.length === 0 ? (
            <div className="text-center py-16">
              <LoadingIndicator />
            </div>
          ) : filteredContent.length > 0 ? (
            <>
              <div
                className={`gap-2 sm:gap-3 ${
                  viewMode === "list"
                    ? "grid grid-cols-1"
                    : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7"
                }`}
              >
                <AnimatePresence>
                  {filteredContent.map((item, index) => (
                    <motion.div
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.02 }}
                      key={item.id}
                    >
                      <ContentCard
                        item={item}
                        onClick={() => setSelectedItem(item)}
                        onLike={(id) => {
                          if (userGlobalData) {
                            dispatch(
                              toggleLikeContent({
                                contentId: id,
                                userId: userGlobalData.user.uid,
                              })
                            );
                          }
                        }}
                        viewMode={viewMode}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div
                ref={loadMoreRef}
                className="h-20 flex items-center justify-center"
              >
                {loading === true && <LoadingIndicator />}
                {!hasMoreContent && (
                  <div className="text-gray-400">All content loaded.</div>
                )}
              </div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-800 rounded-full flex items-center justify-center">
                <Search className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No content found</h3>
              <p className="text-gray-400">
                Try adjusting your search or filters
              </p>
            </motion.div>
          )}
        </motion.div>

        {selectedItem && (
          <ContentModal
            contentId={selectedItem.id}
            source="album"
            albumId={albumId}
            onClose={() => setSelectedItem(null)}
            onLike={(id) => {
              if (userGlobalData) {
                dispatch(
                  toggleLikeContent({
                    contentId: id,
                    userId: userGlobalData.user.uid,
                  })
                );
              }
            }}
          />
        )}

        {notification && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-6 py-3 rounded-full shadow-lg z-[100]"
          >
            {notification}
          </motion.div>
        )}

        {showDeleteModal && selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-4">Delete Content</h2>
              <p className="text-gray-400 mb-6">
                Are you sure you want to delete "{selectedItem.title}"? This
                action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showAddToAlbumModal && selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4"
            onClick={() => setShowAddToAlbumModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold mb-4">Add to Album</h2>
              <p className="text-gray-400 mb-6">
                Select an album to add "{selectedItem.title}" to.
              </p>
              <select
                onChange={(e) => setSelectedAlbumForAddition(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select an album</option>
                {albums.map((album) => (
                  <option key={album.id} value={album.id}>
                    {album.name}
                  </option>
                ))}
              </select>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddToAlbumModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddToAlbum}
                  disabled={!selectedAlbumForAddition}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed px-4 py-2 rounded-lg transition-colors"
                >
                  Add to Album
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {publishItem && (
          <PublishContent
            item={publishItem}
            onClose={() => setPublishItem(null)}
          />
        )}
        </div>
      </PageLayout>
    </div>
  );
};

export default AlbumsPage;
